import {
  useRef, useState, useEffect, useContext, KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { apiCall } from 'providers';
import { CelsiusAiMark, CloseModalX, SendArrowIcon } from '~/icons';
import { getUserProfile } from '~/helpers/userProfile';
import MenuContext from '../../contexts/menuContext';
import CelsiusAiContext from '../../contexts/celsiusAiContext';
import { useIsCelsiusAiAllowed } from './allowedRoutes';
import {
  ChatPanel,
  ChatHeader,
  ChatHeaderTitle,
  ChatTitle,
  ChatTitleBold,
  ChatTitleThin,
  HeaderActions,
  IconButton,
  MessageList,
  UserMessageBubble,
  AiMessage,
  AiIconWrapper,
  AiMessageText,
  InputArea,
  MessageInput,
  SendButton,
  EmptyState,
  EmptyGreeting,
  EmptyPrompt,
  MobileFloatingButton,
  Disclaimer,
} from './styles';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
}

export const CelsiusAIChat = (): JSX.Element | null => {
  const { t } = useTranslation();
  const isAllowed = useIsCelsiusAiAllowed();
  const { isCelsiusOpen, setIsCelsiusOpen } = useContext(CelsiusAiContext);
  const { setMenuToogle } = useContext(MenuContext);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userFirstName = getUserProfile().name?.split(' ')[0] ?? '';

  useEffect(() => {
    if (isCelsiusOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isCelsiusOpen]);

  useEffect(() => {
    if (isCelsiusOpen) {
      setMenuToogle(false);
    }
  }, [isCelsiusOpen]);

  useEffect(() => {
    if (!isAllowed && isCelsiusOpen) {
      setIsCelsiusOpen(false);
    }
  }, [isAllowed]);

  const handleClose = () => {
    setIsCelsiusOpen(false);
  };

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMsg: Message = { id: `${Date.now()}-user`, text, isUser: true };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const resp = await apiCall('/celsius-ai/chat', {
        MESSAGE: text,
        CONVERSATION_ID: conversationId,
      });
      setConversationId(resp.CONVERSATION_ID);
      const agentMsg: Message = { id: `${Date.now()}-agent`, text: resp.RESPONSE, isUser: false };
      setMessages((prev) => [...prev, agentMsg]);
    } catch {
      const errMsg: Message = {
        id: `${Date.now()}-err`,
        text: t('celsiusAiErro'),
        isUser: false,
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') sendMessage();
  };

  if (!isAllowed) return null;

  return (
    <>
      {!isCelsiusOpen && (
        <MobileFloatingButton
          onClick={() => setIsCelsiusOpen(true)}
          title={t('celsiusAiAbrir')}
          type="button"
          data-testid="celsius-ai-open-mobile"
        >
          <CelsiusAiMark size={28} white />
        </MobileFloatingButton>
      )}

      <ChatPanel isOpen={isCelsiusOpen} data-testid="celsius-ai-panel">
        <ChatHeader>
          <ChatHeaderTitle>
            <CelsiusAiMark size={23} white />
            <ChatTitle>
              <ChatTitleBold>celsius</ChatTitleBold>
              <ChatTitleThin>AI</ChatTitleThin>
            </ChatTitle>
          </ChatHeaderTitle>
          <HeaderActions>
            <IconButton onClick={handleClose} title={t('celsiusAiFechar')} data-testid="celsius-ai-close">
              <CloseModalX />
            </IconButton>
          </HeaderActions>
        </ChatHeader>

        <MessageList>
          {messages.length === 0 ? (
            <EmptyState>
              <EmptyGreeting>{t('celsiusAiGreeting', { name: userFirstName })}</EmptyGreeting>
              <EmptyPrompt>{t('celsiusAiEmptyState')}</EmptyPrompt>
            </EmptyState>
          ) : (
            messages.map((msg) => (
              msg.isUser ? (
                <UserMessageBubble key={msg.id}>{msg.text}</UserMessageBubble>
              ) : (
                <AiMessage key={msg.id}>
                  <AiIconWrapper>
                    <CelsiusAiMark size={30} />
                  </AiIconWrapper>
                  <AiMessageText>{msg.text}</AiMessageText>
                </AiMessage>
              )
            ))
          )}
          {isLoading && (
            <AiMessage>
              <AiIconWrapper>
                <CelsiusAiMark size={30} spin />
              </AiIconWrapper>
            </AiMessage>
          )}
          <div ref={messagesEndRef} />
        </MessageList>

        <InputArea>
          <MessageInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('celsiusAiPlaceholder')}
            disabled={isLoading}
          />
          <SendButton onClick={sendMessage} disabled={isLoading || !inputValue.trim()}>
            <SendArrowIcon />
          </SendButton>
        </InputArea>
        <Disclaimer>{t('celsiusAiDisclaimer')}</Disclaimer>
      </ChatPanel>
    </>
  );
};
